import React, { useState } from 'react';
import { Alert, Paper } from '@mui/material';
import { Box } from '@mui/system';

import { ComponentQuantityList } from '@paragon/layout-models';

import { getAnalysisSetGuidToComponentDesignGuid } from '../../../store/selectors/analysisSetsSelectors';
import {
  getDownloadRequestForBulk,
  getDownloadRequestGuidTypeForContext,
  getInitiallySelectedComponentDesignGuidToQuantityFromContext,
} from '../../../store/selectors/downloadSelectors';
import { getCommittedComponentDesignsByGuid } from '../../../store/selectors/getComponentDesignsSelectors';
import { useAppSelector } from '../../hooks';
import { DownloadListItems, DownloadListItemsProps } from './DownloadListItems';

type Props = Pick<DownloadListItemsProps, 'onBeforeDownloadBegins' | 'showAppSpinner'>;

export function CustomQuantitiesPanel({ onBeforeDownloadBegins, showAppSpinner }: Props) {
  const initiallySelectedComponentDesignGuidToQuantityFromContext = useAppSelector(
    getInitiallySelectedComponentDesignGuidToQuantityFromContext,
  );
  const downloadRequestForBulk = useAppSelector(getDownloadRequestForBulk);
  const downloadRequestGuidTypeForContext = useAppSelector(getDownloadRequestGuidTypeForContext);
  const analysisSetGuidToComponentDesignGuid = useAppSelector(getAnalysisSetGuidToComponentDesignGuid);
  const componentDesignsByGuid = useAppSelector(getCommittedComponentDesignsByGuid);

  function getInitialCustomQuantities() {
    if (Object.keys(initiallySelectedComponentDesignGuidToQuantityFromContext).length > 0 && downloadRequestForBulk) {
      const initialCustomQuantities: { [guid: string]: number } = {};
      for (const guid of Object.keys(downloadRequestForBulk.guidToQuantity)) {
        const componentDesignGuid =
          downloadRequestForBulk.guidType === 'AnalysisSet' ? analysisSetGuidToComponentDesignGuid[guid] : guid;
        // Components outside the selection are included with a quantity of 0 so they remain reachable via "Show All"
        initialCustomQuantities[guid] =
          componentDesignGuid in initiallySelectedComponentDesignGuidToQuantityFromContext
            ? initiallySelectedComponentDesignGuidToQuantityFromContext[componentDesignGuid]
            : 0;
      }
      return initialCustomQuantities;
    } else return downloadRequestForBulk?.guidToQuantity ?? {};
  }

  const [customQuantities, setCustomQuantities] = useState<{ [guid: string]: number }>(getInitialCustomQuantities());

  // Components at a quantity of 0 are in `customQuantities` only so they can be edited here, so keep them out of
  // everything downstream
  const nonZeroCustomQuantities = Object.fromEntries(
    Object.entries(customQuantities).filter(([_, quantity]) => quantity !== 0),
  );

  const getName =
    downloadRequestGuidTypeForContext === 'AnalysisSet'
      ? (analysisSetGuid: string) =>
          componentDesignsByGuid[analysisSetGuidToComponentDesignGuid[analysisSetGuid]]?.component.name ?? 'Loading...'
      : undefined;

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2 }}>
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <ComponentQuantityList
          quantities={customQuantities}
          setCustomQuantity={(guid, quantity) => setCustomQuantities((oldState) => ({ ...oldState, [guid]: quantity }))}
          getName={getName}
        />
      </Box>

      {Object.keys(nonZeroCustomQuantities).length > 0 ? (
        <DownloadListItems
          sx={{ flexGrow: 1 }}
          mode="custom"
          customQuantities={nonZeroCustomQuantities}
          onBeforeDownloadBegins={onBeforeDownloadBegins}
          showAppSpinner={showAppSpinner}
        />
      ) : (
        <Paper sx={{ flexGrow: 1 }}>
          <Alert severity="info" sx={{ width: '100%' }}>
            Enter a non-zero quantity to download.
          </Alert>
        </Paper>
      )}
    </Box>
  );
}
